import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, RotateCcw, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { publicFileUrl } from "../../lib/publicCampaigns";
import { TeamBioEditor } from "./TeamBioEditor";
import { TeamMemberActions } from "./TeamMemberActions";
import { FocusSlider } from "./FocusSlider";

/**
 * One roster member on /admin/team: live 4:5 preview (with the covered
 * "then" polaroid zone shaded), upload "today" / "then", vertical focus
 * slider, reset-to-site-default, role/bio editor and hide/remove controls.
 * `override` is the backend record; `person.custom` = added from the console.
 */
export const TeamPhotoCard = ({ person, memberKey, override, client, onSaved, onRemoved, canHide = true }) => {
  const nowInput = useRef(null);
  const thenInput = useRef(null);
  const [busy, setBusy] = useState("");
  const [focus, setFocus] = useState({ x: override?.now_focus_x ?? 50, y: override?.now_focus_y ?? 50 });
  // Re-sync only when the photo itself changes (upload / reset), so a slow save response can't yank the slider back mid-drag.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFocus({ x: override?.now_focus_x ?? 50, y: override?.now_focus_y ?? 50 }); }, [override?.now_file_id]);
  const uploaded = Boolean(override?.now_file_id);

  const nowSrc = override?.now_file_id ? publicFileUrl(override.now_file_id, 960) : person.photoNow;
  const thenSrc = override?.then_file_id ? publicFileUrl(override.then_file_id, 480) : person.photoThen;
  const hasOverride = Boolean(override?.now_file_id || override?.then_file_id);

  const upload = async (file, slot) => {
    if (!file) return;
    setBusy(slot);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("purpose", "team_photo");
      const up = await client.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      const body = slot === "now" ? { now_file_id: up.data.id, now_focus_y: 50, now_focus_x: 50 } : { then_file_id: up.data.id };
      const res = await client.put(`/admin/team/photos/${memberKey}`, body);
      onSaved(memberKey, res.data);
      toast.success(`${person.name}: ${slot === "now" ? "today" : "then"} photo updated.`, {
        description: `${up.data.width}×${up.data.height}, ${Math.round(up.data.size / 1024)} KB — live on /team now.`,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy("");
    }
  };

  const saveFocus = async (axis, value) => {
    if (!uploaded) return;
    try {
      const res = await client.put(`/admin/team/photos/${memberKey}`, axis === "y" ? { now_focus_y: value } : { now_focus_x: value });
      onSaved(memberKey, res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the focus point.");
    }
  };

  const reset = async () => {
    setBusy("reset");
    try {
      const res = await client.put(`/admin/team/photos/${memberKey}`, { now_file_id: null, then_file_id: null, now_focus_y: 50, now_focus_x: 50 });
      onSaved(memberKey, res.data);
      toast.success(person.custom ? `${person.name}: photos removed.` : `${person.name} back to the site default photos.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reset.");
    } finally {
      setBusy("");
    }
  };

  const hidden = Boolean(override?.hidden);
  const status = person.custom ? "added from admin" : hasOverride ? "custom photo" : "site default photo";

  return (
    <div className={`grid gap-5 rounded-2xl border bg-bone p-5 sm:grid-cols-[180px_1fr] ${hidden ? "border-dashed border-ink/30 opacity-75" : "border-border"}`} data-testid={`team-photo-card-${memberKey}`}>
      <div className="relative aspect-[4/5] overflow-hidden rounded-md border border-border bg-secondary" data-testid={`team-photo-preview-${memberKey}`}>
        {nowSrc ? (
          <img src={nowSrc} alt={`${person.name} today`} className="h-full w-full object-cover"
            style={{ objectPosition: uploaded ? `${focus.x}% ${focus.y}%` : `${person.nowFocusX ?? 50}% ${person.nowFocusY ?? 50}%` }} />
        ) : (
          <div className="flex h-full items-center justify-center text-olive/60"><ImageOff className="h-6 w-6" strokeWidth={1.5} /></div>
        )}
        {/* Where the "then" polaroid sits on the public page — keep the face above it. */}
        <div className="absolute -left-1 -bottom-2 w-[36%] rotate-[-5deg] rounded-sm border border-dashed border-ink/40 bg-bone/70 p-1 pb-4" aria-hidden="true">
          <div className="aspect-[3/4] overflow-hidden bg-ink/10">
            {thenSrc && <img src={thenSrc} alt="" className="h-full w-full object-cover opacity-80" />}
          </div>
        </div>
      </div>

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-serif text-2xl leading-tight text-ink">{person.name}</p>
          {hidden && <span className="rounded-full bg-ink/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink" data-testid={`team-hidden-badge-${memberKey}`}>hidden</span>}
        </div>
        <p className="text-[13px] italic text-olive">{override?.role || person.role} · {status}</p>

        <div className="mt-4 flex flex-wrap gap-2">
          <input ref={nowInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0], "now")} data-testid={`team-photo-now-input-${memberKey}`} />
          <input ref={thenInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0], "then")} data-testid={`team-photo-then-input-${memberKey}`} />
          <button type="button" disabled={Boolean(busy)} onClick={() => nowInput.current?.click()} data-testid={`team-photo-upload-now-${memberKey}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-[12px] font-semibold text-sand transition hover:bg-forest disabled:opacity-60">
            {busy === "now" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" strokeWidth={2} />}
            Upload today photo
          </button>
          <button type="button" disabled={Boolean(busy)} onClick={() => thenInput.current?.click()} data-testid={`team-photo-upload-then-${memberKey}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-ink/25 bg-bone px-4 py-2 text-[12px] font-semibold text-ink transition hover:border-ink disabled:opacity-60">
            {busy === "then" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" strokeWidth={2} />}
            Upload then photo
          </button>
          {hasOverride && (
            <button type="button" disabled={Boolean(busy)} onClick={reset} data-testid={`team-photo-reset-${memberKey}`}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[12px] font-semibold text-olive transition hover:text-ink disabled:opacity-60">
              <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} />
              {person.custom ? "Remove photos" : "Reset to site default"}
            </button>
          )}
        </div>

        <div className="mt-5 space-y-3">
          <FocusSlider axis="y" value={focus.y} disabled={!uploaded} memberKey={memberKey}
            onChange={(v) => setFocus((f) => ({ ...f, y: v }))} onCommit={(v) => saveFocus("y", v)} />
          <FocusSlider axis="x" value={focus.x} disabled={!uploaded} memberKey={memberKey}
            onChange={(v) => setFocus((f) => ({ ...f, x: v }))} onCommit={(v) => saveFocus("x", v)} />
        </div>
        <span className="mt-2 block text-[11px] text-olive">
          Nudge the photo until the face, or anything you want visible, sits clear of the dashed polaroid. Saves when you let go.
          {uploaded ? "" : " (Upload a today photo first; site default photos are fixed.)"}
        </span>

        <TeamBioEditor person={person} memberKey={memberKey} override={override} client={client} onSaved={onSaved} />
        {canHide && <TeamMemberActions person={person} memberKey={memberKey} override={override} client={client} onSaved={onSaved} onRemoved={onRemoved} />}
      </div>
    </div>
  );
};
